import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { BarChart } from '../../components/charts/BarChart.jsx';
import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { LineChart } from '../../components/charts/LineChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';
import {
  toEmployeeStatusSeries,
  toHeadcountSeries,
  toPayrollTrendSeries,
  toRecruitmentFunnelSeries,
} from './hrCharts.js';

const MONTH_OPTIONS = [6, 12, 24];
const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export function HrDashboardPage() {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const [months, setMonths] = useState(12);

  const headcount = useQuery({ queryKey: ['hr', 'headcount'], queryFn: () => hrApi.headcountByDepartment().then((r) => r.items) });
  const status = useQuery({ queryKey: ['hr', 'employee-status'], queryFn: () => hrApi.employeeStatus().then((r) => r.items) });
  const payroll = useQuery({ queryKey: ['hr', 'payroll-trend', months], queryFn: () => hrApi.payrollTrend({ months }).then((r) => r.items) });
  const funnel = useQuery({ queryKey: ['hr', 'recruitment-funnel'], queryFn: () => hrApi.recruitmentFunnel().then((r) => r.items) });

  const headcountSeries = toHeadcountSeries(headcount.data?.departments);
  const statusSeries = toEmployeeStatusSeries(status.data?.statuses);
  const payrollSeries = toPayrollTrendSeries(payroll.data?.months);
  const funnelSeries = toRecruitmentFunnelSeries(funnel.data?.stages);

  const totalHeadcount = sum(headcountSeries.counts);
  const latestPayroll = [...payrollSeries.gross].reverse().find((v) => v > 0) || 0;
  const totalApplications = sum(funnelSeries.counts);
  const hiredCount = (funnel.data?.stages || []).find((s) => s.stage === 'HIRED')?.count || 0;

  const accent = palette?.accent || '#1d4ed8';
  const success = palette?.series?.[1] || '#059669';
  const expense = palette?.expense || '#dc2626';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hr.dashboard.title')}</h1>
          <p className="muted">{t('hr.dashboard.subtitle')}</p>
        </div>
        <div className="period-switch">
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              type="button"
              className={m === months ? 'period-btn active' : 'period-btn'}
              onClick={() => setMonths(m)}
            >
              {t('hr.dashboard.lastMonths', { count: m })}
            </button>
          ))}
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('hr.metric.headcount')} value={formatNumber(totalHeadcount, 0)} />
        <MetricCard label={t('hr.metric.payroll')} value={formatCurrency(latestPayroll)} />
        <MetricCard label={t('hr.metric.applications')} value={formatNumber(totalApplications, 0)} />
        <MetricCard label={t('hr.metric.hired')} value={formatNumber(hiredCount, 0)} trendVariant="success" />
      </div>

      <div className="dashboard-grid">
        <ChartCard className="span-2" title={t('hr.chart.payroll')} subtitle={t('hr.chart.payrollSub')}>
          {payroll.isLoading ? <Spinner /> : payrollSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <LineChart
              labels={payrollSeries.labels}
              datasets={[
                { label: t('hr.chart.gross'), data: payrollSeries.gross, color: accent, fill: true },
                { label: t('hr.chart.net'), data: payrollSeries.net, color: success },
                { label: t('hr.chart.tax'), data: payrollSeries.tax, color: expense },
              ]}
            />
          )}
        </ChartCard>

        <ChartCard title={t('hr.chart.headcount')}>
          {headcount.isLoading ? <Spinner /> : headcountSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={headcountSeries.labels} datasets={[{ label: t('hr.metric.headcount'), data: headcountSeries.counts, color: accent }]} />
          )}
        </ChartCard>

        <ChartCard title={t('hr.chart.employeeStatus')}>
          {status.isLoading ? <Spinner /> : statusSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <DonutChart labels={statusSeries.labels} values={statusSeries.counts} />
          )}
        </ChartCard>

        <ChartCard className="span-2" title={t('hr.chart.recruitment')}>
          {funnel.isLoading ? <Spinner /> : funnelSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={funnelSeries.labels} datasets={[{ label: t('hr.chart.applications'), data: funnelSeries.counts, color: accent }]} />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
